import axios from 'axios';
import { API_BASE_URL } from '../constants/config';

export interface RawItem {
  id: number;
  name: string;
  quantityUnit: string;
  category: string;
}

export interface RawItemTransaction {
  id: number;
  rawItemId: number;
  rawItemName: string;
  transactionType: string;
  quantity: number;
  totalAmount: number;
  transactionDate: string;
  userId: number | null;
  userName: string | null;
  notes: string | null;
  vendor: string | null;
  paymentMethod: string | null;
}

const RAW_ITEMS_API = `${API_BASE_URL}/raw-items`;
const RAW_ITEM_TRANSACTIONS_API = `${API_BASE_URL}/raw-item-transactions`;

// Raw Items API calls
export const rawItemService = {
  getAllRawItems: async (): Promise<RawItem[]> => {
    const response = await axios.get(RAW_ITEMS_API);
    return response.data;
  },

  getRawItemById: async (id: number): Promise<RawItem> => {
    const response = await axios.get(`${RAW_ITEMS_API}/${id}`);
    return response.data;
  },

  createRawItem: async (rawItem: Omit<RawItem, 'id'>): Promise<RawItem> => {
    const response = await axios.post(RAW_ITEMS_API, rawItem);
    return response.data;
  },

  updateRawItem: async (id: number, rawItem: Partial<RawItem>): Promise<RawItem> => {
    const response = await axios.put(`${RAW_ITEMS_API}/${id}`, rawItem);
    return response.data;
  },

  deleteRawItem: async (id: number): Promise<void> => {
    await axios.delete(`${RAW_ITEMS_API}/${id}`);
  },

  searchRawItems: async (searchTerm: string): Promise<RawItem[]> => {
    const response = await axios.get(`${RAW_ITEMS_API}/search?searchTerm=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  getRawItemsByCategory: async (category: string): Promise<RawItem[]> => {
    const response = await axios.get(`${RAW_ITEMS_API}/category/${encodeURIComponent(category)}`);
    return response.data;
  }
};

// Raw Item Transactions API calls
export const rawItemTransactionService = {
  getAllRawItemTransactions: async (): Promise<RawItemTransaction[]> => {
    const response = await axios.get(RAW_ITEM_TRANSACTIONS_API);
    return response.data;
  },

  getRawItemTransactionById: async (id: number): Promise<RawItemTransaction> => {
    const response = await axios.get(`${RAW_ITEM_TRANSACTIONS_API}/${id}`);
    return response.data;
  },

  createRawItemTransaction: async (transaction: {
    rawItemId: number;
    transactionType: string;
    quantity: number;
    totalAmount: number;
    notes?: string;
    transactionDate?: string;
    userId?: number | null;
    vendor?: string;
    paymentMethod?: string;
  }): Promise<RawItemTransaction> => {
    const token = localStorage.getItem('token');
    const response = await axios.post(RAW_ITEM_TRANSACTIONS_API, transaction, {
      headers: { Authorization: token }
    });
    return response.data;
  },

  updateRawItemTransaction: async (id: number, transaction: Partial<RawItemTransaction>): Promise<RawItemTransaction> => {
    const token = localStorage.getItem('token');
    const response = await axios.put(`${RAW_ITEM_TRANSACTIONS_API}/${id}`, transaction, {
      headers: { Authorization: token }
    });
    return response.data;
  },

  deleteRawItemTransaction: async (id: number): Promise<void> => {
    const token = localStorage.getItem('token');
    await axios.delete(`${RAW_ITEM_TRANSACTIONS_API}/${id}`, {
      headers: { Authorization: token }
    });
  },

  getRawItemTransactionsByRawItemId: async (rawItemId: number): Promise<RawItemTransaction[]> => {
    const response = await axios.get(`${RAW_ITEM_TRANSACTIONS_API}/raw-item/${rawItemId}`);
    return response.data;
  },



  getRawItemTransactionsByUserId: async (userId: number): Promise<RawItemTransaction[]> => {
    const response = await axios.get(`${RAW_ITEM_TRANSACTIONS_API}/user/${userId}`);
    return response.data;
  },

  searchRawItemTransactions: async (searchTerm: string): Promise<RawItemTransaction[]> => {
    const response = await axios.get(`${RAW_ITEM_TRANSACTIONS_API}/search?searchTerm=${encodeURIComponent(searchTerm)}`);
    return response.data;
  }
};
